import { Facebook, Twitter, Linkedin, Instagram, Youtube } from "lucide-react";

// Simple link component for SSR compatibility
const FooterLink = ({ to, children, className, title, ...props }: { 
  to: string; 
  children: React.ReactNode; 
  className?: string; 
  title?: string;
  [key: string]: any;
}) => {
  return (
    <a 
      href={to} 
      className={className} 
      title={title}
      {...props}
    >
      {children}
    </a>
  );
};

const Footer = () => {
  return (
    <footer className="bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12 max-w-none 2xl:max-w-[1600px]">
        <div className="py-12 md:py-16 2xl:py-20 border-t border-gray-100">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
            <div className="col-span-1">
              <h3 className="text-xl font-bold text-black">Assembly</h3>
              <p className="mt-4 text-sm text-gray-600">
                The leading employee recognition platform that drives performance through meaningful recognition, feedback, and community.
              </p>
              <div className="mt-6 flex space-x-4">
                <a href="https://www.facebook.com/gotojoinassembly" target="_blank" rel="noopener noreferrer" aria-label="Assembly's Facebook page" title="Follow Assembly on Facebook" className="text-gray-600 hover:text-assembly-orange transition-colors">
                  <Facebook className="h-5 w-5" />
                </a>
                <a href="https://twitter.com/join_assembly" target="_blank" rel="noopener noreferrer" aria-label="Assembly's Twitter page" title="Follow Assembly on Twitter" className="text-gray-600 hover:text-assembly-orange transition-colors">
                  <Twitter className="h-5 w-5" />
                </a>
                <a href="https://www.linkedin.com/company/joinassembly/" target="_blank" rel="noopener noreferrer" aria-label="Assembly's LinkedIn page" title="Follow Assembly on LinkedIn" className="text-gray-600 hover:text-assembly-orange transition-colors">
                  <Linkedin className="h-5 w-5" />
                </a>
                <a href="https://www.instagram.com/join_assembly/" target="_blank" rel="noopener noreferrer" aria-label="Assembly's Instagram page" title="Follow Assembly on Instagram" className="text-gray-600 hover:text-assembly-orange transition-colors">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="https://www.youtube.com/channel/UC8iP6SSNi5eAE_Nd5EaShCQ" target="_blank" rel="noopener noreferrer" aria-label="Assembly's YouTube channel" title="Watch Assembly on YouTube" className="text-gray-600 hover:text-assembly-orange transition-colors">
                  <Youtube className="h-5 w-5" />
                </a>
              </div>
              <div className="mt-6 flex items-center gap-4">
                <img 
                  src="/lovable-uploads/16ab782f-706b-47f6-9ad2-7b14ef360ceb.png" 
                  alt="SOC 2 Type II Compliant" 
                  className="h-8 w-auto"
                />
                <img 
                  src="/lovable-uploads/b5dea732-782e-4655-8a27-8253f6834e70.png" 
                  alt="GDPR Compliant" 
                  className="h-8 w-auto"
                />
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase">
                Product
              </h3>
              <ul className="mt-4 space-y-2">
                <li><FooterLink to="/employee-recognition/peer-to-peer" title="Learn about peer-to-peer recognition features" className="text-sm text-gray-600 hover:text-assembly-orange">Peer-to-Peer Recognition</FooterLink></li>
                <li><FooterLink to="/employee-recognition/awards" title="Discover awards and recognition programs" className="text-sm text-gray-600 hover:text-assembly-orange">Awards</FooterLink></li>
                <li><FooterLink to="/employee-recognition/milestones" title="Celebrate milestones" className="text-sm text-gray-600 hover:text-assembly-orange">Milestones</FooterLink></li>
                <li><FooterLink to="/employee-recognition/rewards" title="Explore rewards and incentive programs" className="text-sm text-gray-600 hover:text-assembly-orange">Rewards</FooterLink></li>
                <li><FooterLink to="/employee-recognition/challenges" title="Create engaging team challenges" className="text-sm text-gray-600 hover:text-assembly-orange">Challenges</FooterLink></li>
                <li><FooterLink to="/employee-engagement/announcements" title="Share announcements with your organization" className="text-sm text-gray-600 hover:text-assembly-orange">Announcements</FooterLink></li>
                <li><FooterLink to="/integrations" title="View available integrations and connectors" className="text-sm text-gray-600 hover:text-assembly-orange">Integrations</FooterLink></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase">
                Company
              </h3>
              <ul className="mt-4 space-y-2">
                <li><FooterLink to="/company/careers" title="Join our team - view open positions" className="text-sm text-gray-600 hover:text-assembly-orange">Careers</FooterLink></li>
                <li><FooterLink to="/press" title="Read press releases and news coverage" className="text-sm text-gray-600 hover:text-assembly-orange">Press</FooterLink></li>
                <li><FooterLink to="/security-policy" title="View our security policy and compliance information" className="text-sm text-gray-600 hover:text-assembly-orange">Security & Compliance</FooterLink></li>
                <li><FooterLink to="/vulnerability-disclosure-policy" title="Learn about our vulnerability disclosure policy" className="text-sm text-gray-600 hover:text-assembly-orange">Disclosure Policy</FooterLink></li>
                <li><a href="https://trust.joinassembly.com" target="_blank" rel="noopener noreferrer" title="Visit Assembly's trust center for security information" className="text-sm text-gray-600 hover:text-assembly-orange">Trust Center</a></li>
                <li><FooterLink to="/status" title="Check Assembly's system status and uptime" className="text-sm text-gray-600 hover:text-assembly-orange">System Status</FooterLink></li>
              </ul>
            </div>


            <div>
              <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase">
                Resources
              </h3>
              <ul className="mt-4 space-y-2">
                <li><FooterLink to="/book-demo" title="Schedule a personalized demo of Assembly" className="text-sm text-gray-700 hover:text-assembly-orange">Book a Demo</FooterLink></li>
                <li><FooterLink to="/employee-recognition-program-guide-assembly/introduction" title="Complete guide to employee recognition programs" className="text-sm text-gray-700 hover:text-assembly-orange">Employee Recognition Guide</FooterLink></li>
                <li><FooterLink to="/best/employee-recognition-software" title="Compare the best employee recognition software platforms" className="text-sm text-gray-700 hover:text-assembly-orange">Best Recognition Software</FooterLink></li>
                <li><FooterLink to="/resources/case-studies" title="Read customer success stories and case studies" className="text-sm text-gray-700 hover:text-assembly-orange">Case Studies</FooterLink></li>
                <li><FooterLink to="/resources/whitepapers" title="Download research white papers and reports" className="text-sm text-gray-700 hover:text-assembly-orange">White Papers</FooterLink></li>
                <li><a href="https://help.joinassembly.com" target="_blank" rel="noopener noreferrer" title="Get help and support for Assembly" className="text-sm text-gray-700 hover:text-assembly-orange">Help Center</a></li>
                <li><a href="https://www.quantumworkplace.com/about/contact-us" target="_blank" rel="noopener noreferrer" title="Contact Assembly support team" className="text-sm text-gray-700 hover:text-assembly-orange">Contact Us</a></li>
                <li><a href="/sitemap.xml" title="View website sitemap" className="text-sm text-gray-700 hover:text-assembly-orange">Sitemap</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase">
                Comparison
              </h3>
              <ul className="mt-4 space-y-2">
                <li><FooterLink to="/compare/assembly-vs-bonusly" title="Compare Assembly vs Bonusly employee recognition platforms" className="text-sm text-gray-700 hover:text-assembly-orange">Assembly vs Bonusly</FooterLink></li>
                <li><FooterLink to="/compare/assembly-vs-nectar" title="Compare Assembly vs Nectar employee recognition platforms" className="text-sm text-gray-700 hover:text-assembly-orange">Assembly vs Nectar</FooterLink></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="py-8 border-t border-gray-100">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-700">
              &copy; {new Date().getFullYear()} CarrotHR Inc dba Assembly. All rights reserved.
              <br />
              13810 FNB Pkwy, Suite 401, Omaha, NE 68154
            </p>
            <div className="mt-4 md:mt-0 flex flex-wrap space-x-4">
              <FooterLink to="/terms-of-service" title="Read our terms of service" className="text-sm text-gray-600 hover:text-assembly-orange">Terms</FooterLink>
              <a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=b8386cea-0fe7-4b37-80c0-91482f04c460" target="_blank" rel="noopener noreferrer" title="View our cookie policy" className="text-sm text-gray-600 hover:text-assembly-orange">Cookie Policy</a>
              <FooterLink to="/privacy-policy" title="Read our privacy policy" className="text-sm text-gray-600 hover:text-assembly-orange">Privacy Policy</FooterLink>
              <FooterLink to="/ccpa-policy" title="California Consumer Privacy Act information" className="text-sm text-gray-600 hover:text-assembly-orange">CCPA</FooterLink>
              <FooterLink to="/gdpr-protection" title="General Data Protection Regulation compliance" className="text-sm text-gray-600 hover:text-assembly-orange">GDPR</FooterLink>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
